import { inject } from '@angular/core';
import {
  CanActivateFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { RolesDefaultRoutes, RoutePaths } from '../app.routes';
import { SystemUser, SystemUserFeatures } from '../models/systemUser.model';
import { map } from 'rxjs';

/**
 * The user's landing route, feature-aware: when the role's default page belongs to a disabled
 * feature (My Day needs gamification), fall back to the pre-My-Day default so the root redirect
 * can never loop between the guard and a hidden page.
 */
function resolveDefaultRoute(user: SystemUser): string {
  const roleDefault = Object.entries(RolesDefaultRoutes).find(([, roleIds]) =>
    (roleIds as number[]).includes(user.currentPharmacy?.roleId ?? 0),
  )?.[0];
  // Admins have no RolesDefaultRoutes entry; their landing page is the Overview.
  let route = roleDefault ?? (user.isAdmin ? RoutePaths.Overview.Path : '');
  // My Day belongs to gamification; with the feature off, fall back to the pre-My-Day default.
  if (
    route === RoutePaths.MyDay.Path &&
    user.features?.gamification === false
  ) {
    route = RoutePaths.RequestAcknowledgedList.Path;
  }
  return route;
}

/**
 * Guard to ensure authentication is valid
 * @param route - The current route
 * @param state - The current state
 * @returns True if authentication is valid, otherwise redirects to error page
 */
export const AuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  return authService.currentUser$.pipe(
    map((user) => {
      if (!user) {
        router.navigate([RoutePaths.Error.Path], {
          queryParams: {
            reason: 'authentication_failed',
            code: 401,
          },
        });
        return false;
      }

      // Feature switches apply to everyone, admins included: when a feature's backing service
      // is off, its pages redirect to the user's default route instead of rendering dead data.
      const feature = route.data['feature'] as
        | keyof SystemUserFeatures
        | undefined;
      if (feature && user.features?.[feature] === false) {
        const defaultRoute = resolveDefaultRoute(user);
        if (defaultRoute && defaultRoute !== route.routeConfig?.path) {
          router.navigate([defaultRoute]);
        } else {
          router.navigate([RoutePaths.Error.Path], {
            queryParams: { reason: 'unauthorized', code: 403 },
          });
        }
        return false;
      }

      if (user.isAdmin) {
        return true;
      }
      const allowedRoles = route.data['allowedRoles'] as number[];
      if (
        allowedRoles &&
        !allowedRoles.includes(user.currentPharmacy?.roleId ?? 0)
      ) {
        const defaultRoute = resolveDefaultRoute(user);
        if (defaultRoute) {
          router.navigate([defaultRoute]);
          return false;
        }
        router.navigate([RoutePaths.Error.Path], {
          queryParams: {
            reason: 'unauthorized',
            code: 403,
          },
        });
        return false;
      }

      const requiresCommercializationAccess = route.data[
        'requiresCommercializationAccess'
      ] as boolean;
      if (
        requiresCommercializationAccess &&
        !user.currentPharmacy?.commercializationAccess
      ) {
        router.navigate([RoutePaths.Error.Path], {
          queryParams: { reason: 'unauthorized', code: 403 },
        });
        return false;
      }

      return true;
    }),
  );
};
